import React from "react";
import styles from "./DisplayDNA.module.css";

/**
 * ORF codons directly under the sequence (above features).
 * - No wrapper behind AA boxes
 * - Uses EXCLUSIVE end (endExcl)
 * - Start codon = green, Stop codon = red (by position and by AA="Stop")
 * - ✅ When a line breaks inside a codon, draw codon fragments on both lines,
 *   and split the 3-letter label accordingly (e.g., A/TG -> M/et, AT/G -> Me/t).
 *   For Stop, always render a centered "*".
 */

const CODON_TO_AA3 = {
  TTT: "Phe",
  TTC: "Phe",
  TTA: "Leu",
  TTG: "Leu",
  CTT: "Leu",
  CTC: "Leu",
  CTA: "Leu",
  CTG: "Leu",
  ATT: "Ile",
  ATC: "Ile",
  ATA: "Ile",
  ATG: "Met",
  GTT: "Val",
  GTC: "Val",
  GTA: "Val",
  GTG: "Val",

  TCT: "Ser",
  TCC: "Ser",
  TCA: "Ser",
  TCG: "Ser",
  CCT: "Pro",
  CCC: "Pro",
  CCA: "Pro",
  CCG: "Pro",
  ACT: "Thr",
  ACC: "Thr",
  ACA: "Thr",
  ACG: "Thr",
  GCT: "Ala",
  GCC: "Ala",
  GCA: "Ala",
  GCG: "Ala",

  TAT: "Tyr",
  TAC: "Tyr",
  TAA: "Stop",
  TAG: "Stop",
  CAT: "His",
  CAC: "His",
  CAA: "Gln",
  CAG: "Gln",
  AAT: "Asn",
  AAC: "Asn",
  AAA: "Lys",
  AAG: "Lys",
  GAT: "Asp",
  GAC: "Asp",
  GAA: "Glu",
  GAG: "Glu",

  TGT: "Cys",
  TGC: "Cys",
  TGA: "Stop",
  TGG: "Trp",
  CGT: "Arg",
  CGC: "Arg",
  CGA: "Arg",
  CGG: "Arg",
  AGT: "Ser",
  AGC: "Ser",
  AGA: "Arg",
  AGG: "Arg",
  GGT: "Gly",
  GGC: "Gly",
  GGA: "Gly",
  GGG: "Gly",
};

function mod(a, n) {
  const r = a % n;
  return r < 0 ? r + n : r;
}
function revCompTriplet(triplet) {
  const compMap = { A: "T", T: "A", G: "C", C: "G" };
  const t = (triplet || "").toUpperCase().replace(/[^ATGC]/g, "N");
  return t
    .split("")
    .reverse()
    .map((c) => compMap[c] || "N")
    .join("");
}
function rangesOverlap(a1, a2ex, b1, b2ex) {
  // [a1, a2ex) vs [b1, b2ex)
  return !(a2ex <= b1 || b2ex <= a1);
}

export default function OrfLanes({
  row,
  orfs = [],
  charAdvancePx,
  onSelectRange,
  topSequence = "",
}) {
  const lanes = React.useMemo(
    () => computeOrfTracksForRow(row, orfs),
    [row, orfs]
  );
  if (!lanes.length) return null;

  const adv = Math.max(1, charAdvancePx || 1);
  const rowStart = row.start;
  const rowEndExcl = row.end; // exclusive

  return (
    <div className={styles.orfsBlock} style={{ "--lanes": lanes.length }}>
      {lanes.map((lane, laneIdx) => (
        <div
          key={`orflane-${laneIdx}`}
          className={styles.orfLaneRow}
          style={{
            top: `calc(${laneIdx} * (var(--feature-h) + var(--feature-gap)))`,
          }}
        >
          {lane.map((seg) => {
            // visible span intersection with row, EXCLUSIVE end
            const visStart = Math.max(seg.absStart, rowStart);
            const visEndExcl = Math.min(seg.absEndExcl, rowEndExcl);
            if (visStart >= visEndExcl) return null;

            // Align codon starts to ORF phase.
            // For forward: anchor = ORF start.
            // For reverse: anchor = last codon start = absEndExcl - 3.
            const anchor =
              seg.direction === "forward" ? seg.absStart : seg.absEndExcl - 3;

            // Allow the first codon to start before visStart so we can render the left fragment
            let firstStart = visStart - mod(visStart - anchor, 3);
            if (firstStart < seg.absStart) firstStart = seg.absStart;

            const lastStart = seg.absEndExcl - 3; // last valid codon start

            const aaBoxes = [];
            for (
              let codonStartAbs = firstStart;
              codonStartAbs <= lastStart;
              codonStartAbs += 3
            ) {
              if (codonStartAbs >= visEndExcl) break;

              const codonEndExcl = codonStartAbs + 3;

              // Draw ONLY the part of this codon that lies within this row
              const drawStart = Math.max(codonStartAbs, rowStart);
              const drawEndExcl = Math.min(codonEndExcl, rowEndExcl);
              const ntCount = drawEndExcl - drawStart; // 1..3 on visible rows
              if (ntCount <= 0) continue;

              const leftPx = Math.round((drawStart - rowStart) * adv);
              const widthPx = Math.max(1, Math.round(ntCount * adv));

              let codon = topSequence.substr(codonStartAbs, 3).toUpperCase();
              if (seg.direction === "reverse") {
                codon = revCompTriplet(codon);
              }
              const aa3 = CODON_TO_AA3[codon] || "Xxx";

              // Position-based start/stop with EXCLUSIVE end
              const isStartCodon =
                (seg.direction === "forward" &&
                  codonStartAbs === seg.absStart) ||
                (seg.direction === "reverse" &&
                  codonStartAbs === seg.absEndExcl - 3);

              const isStopByPos =
                (seg.direction === "forward" &&
                  codonStartAbs === seg.absEndExcl - 3) ||
                (seg.direction === "reverse" && codonStartAbs === seg.absStart);

              const isStopCodon = isStopByPos || aa3 === "Stop";

              // ------- label text (Stop -> centered '*') -------
              let labelPart;
              if (aa3 === "Stop") {
                labelPart = "*"; // always show centered star on every fragment
              } else {
                // Split label across a line break proportionally to nucleotides shown
                const offsetWithinCodon = Math.max(
                  0,
                  Math.min(3, drawStart - codonStartAbs)
                );
                labelPart = aa3.slice(
                  offsetWithinCodon,
                  offsetWithinCodon + ntCount
                );
              }

              aaBoxes.push(
                <div
                  key={`aa-${seg.id}-${row.rowIndex}-${codonStartAbs}-${drawStart}`}
                  className={`${styles.orfAa} ${
                    seg.direction === "forward"
                      ? styles.aaForward
                      : styles.aaReverse
                  } ${isStartCodon ? styles.aaStart : ""} ${
                    isStopCodon ? styles.aaStop : ""
                  }`}
                  style={{ left: `${leftPx}px`, width: `${widthPx}px` }}
                  title={`${aa3} (${codon})`}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    // Select EXACT ORF span: [start, endExcl-1]
                    onSelectRange?.({
                      start: seg.absStart,
                      end: seg.absEndExcl - 1,
                    });
                  }}
                >
                  <span className={styles.orfAaLabel}>{labelPart}</span>
                </div>
              );
            }

            return (
              <React.Fragment key={`${seg.id}-${row.rowIndex}`}>
                {aaBoxes}
              </React.Fragment>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function computeOrfTracksForRow(row, orfs) {
  const rowStart = row.start;
  const rowEndExcl = row.end; // exclusive

  const candidates = orfs
    // keep ORFs that overlap the row (exclusive ends)
    .filter((o) => o.endExcl > rowStart && o.start < rowEndExcl)
    .map((o) => ({
      id: o.id,
      frame: o.frame,
      direction: o.direction || (o.frame >= 3 ? "reverse" : "forward"),
      strand: o.strand || (o.frame >= 3 ? "bottom" : "top"),
      absStart: Math.min(o.start ?? 0, o.endExcl ?? 0),
      absEndExcl: Math.max(o.start ?? 0, o.endExcl ?? 0),
      len: Math.max(0, (o.endExcl ?? 0) - (o.start ?? 0)),
    }))
    // longer ORFs nearer to the sequence
    .sort((a, b) => b.len - a.len || a.absStart - b.absStart);

  const lanes = [];
  for (const seg of candidates) {
    let placed = false;
    for (const lane of lanes) {
      if (
        !lane.some((x) =>
          rangesOverlap(x.absStart, x.absEndExcl, seg.absStart, seg.absEndExcl)
        )
      ) {
        lane.push(seg);
        placed = true;
        break;
      }
    }
    if (!placed) lanes.push([seg]);
  }
  return lanes;
}
